<template>
  <div class="review-and-complete">
    <div class="contact-information-container">
      <review-and-complete-shipping-address
        v-if="physicalRedemptionsExist"
        :shipping-address="physicalRecipient"
        @edit="handleEditShippingAddress"
      />
      <div class="confirm-email-address">
        <h4
          class="confirm-email-address-title"
          v-text="$t('review-and-complete-digital-address-header')"
        />
        <p>
          <span class="email-address">
            {{ digitalRecipient.email }}
          </span>
        </p>
      </div>
    </div>
    <div class="order-review-and-complete-container">
      <checkout-review
        :heading="$t('review-and-complete-checkout-review-header')"
      />
      <div class="review-and-complete-tos-container">
        <v-checkbox
          id="review-and-complete-tos-checkbox"
          v-model="termsOfServiceAgreedTo"
          class="review-and-complete-tos-checkbox"
          name="review-and-complete-tos-checkbox"
          tabindex="0"
          hide-details
        />
        <div class="review-and-complete-tos-label">
          <label for="review-and-complete-tos-checkbox">
            {{ $t('review-and-complete-acknowledgment.start') }}
          </label>
          <router-link
            to="/terms-of-service"
            target="_blank"
            class="link"
          >
            {{ $t('review-and-complete-acknowledgment.terms-of-service') }}
          </router-link>
          {{ $t('review-and-complete-acknowledgment.join') }}
          <a
            :href="privacyUrl"
            target="_blank"
            class="link"
          >
            {{ $t('review-and-complete-acknowledgment.privacy-notice') }}
          </a>
        </div>
      </div>
      <captcha-container
        v-if="isPlaceOrderRecaptchaSplitEnabled"
        action="rl_place_order"
      >
        <checkout-review-buttons
          id="check-out-review-buttons-with-captcha"
          slot-scope="{ generateCaptchaToken, captchaIsLoading }"
          :complete-button-is-disabled="
            completeButtonIsDisabled || captchaIsLoading
          "
          :complete-button-text="proceedButtonText"
          :show-loading="orderIsProcessing"
          @proceed="placeOrder(generateCaptchaToken)"
        />
      </captcha-container>
      <checkout-review-buttons
        v-else
        id="check-out-review-buttons-no-captcha"
        :complete-button-is-disabled="completeButtonIsDisabled"
        :complete-button-text="proceedButtonText"
        :show-loading="orderIsProcessing"
        @proceed="placeOrder"
      />
    </div>

    <leave-while-order-processing-dialog
      v-model="showLeaveWhileProcessingConfirmation"
      @cancel="cancelLeaveWhileProcessing"
      @confirm="confirmLeaveWhileProcessing"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import * as actionTypes from '@/store/action-types'
import * as getTypes from '@/store/get-types'
import { CHECKOUT_ROUTE } from '@/router/routes/route-names'
import { RL_PLACE_ORDER_RECAPTCHA } from '@/plugins/split/ActiveSplits'
import {
  CHECKOUT_STEP_COMPLETED,
  CHECKOUT_STEP_VIEWED
} from '@/plugins/segment/SegmentConstants'
import LineItem from '@/components/checkout/line_item'
import ReviewAndCompleteShippingAddress from '@/components/checkout/ReviewAndCompleteShippingAddress'
import CheckoutReview from '@/components/checkout/CheckoutReview'
import CheckoutReviewButtons from '@/components/checkout/CheckoutReviewButtons'
import CaptchaContainer from '@/components/shared/captcha/CaptchaContainer'
import LeaveWhileOrderProcessingDialog from '@/components/checkout/LeaveWhileOrderProcessingDialog'

const CHECKOUT_STEP_NUMBER = 2

export default {
  name: 'ReviewAndComplete',
  components: {
    LeaveWhileOrderProcessingDialog,
    CaptchaContainer,
    CheckoutReview,
    CheckoutReviewButtons,
    ReviewAndCompleteShippingAddress
  },
  beforeRouteLeave (to, from, next) {
    if (this.orderIsProcessing) {
      this.showLeaveWhileProcessingConfirmation = true
      this.leaveWhileProcessingNextRoute = next
      next(false)
    } else {
      next()
    }
  },
  data: () => ({
    leaveWhileProcessingNextRoute: null,
    originalWindowEvents: {
      onBeforeUnload: null,
      onUnload: null
    },
    showLeaveWhileProcessingConfirmation: false,
    termsOfServiceAgreedTo: false
  }),
  computed: {
    completeButtonIsDisabled () {
      return (
        !this.termsOfServiceAgreedTo ||
        this.orderIsProcessing ||
        this.cartItems.length < 1
      )
    },
    proceedButtonText () {
      return this.orderIsProcessing
        ? this.$t('checkout-processing')
        : this.$t('review-and-complete-button')
    },
    isPlaceOrderRecaptchaSplitEnabled () {
      return this.$isSplitEnabled(RL_PLACE_ORDER_RECAPTCHA)
    },
    privacyUrl () {
      return `${process.env.VUE_APP_TANGO_CARD_WEBSITE_URL}/privacy-notice/`
    },
    ...mapGetters({
      physicalRedemptionsExist: getTypes.GET_PHYSICAL_CART_ITEMS_EXIST,
      physicalRecipient: getTypes.GET_PHYSICAL_RECIPIENT,
      digitalRecipient: getTypes.GET_DIGITAL_RECIPIENT,
      physicalCartItems: getTypes.GET_PHYSICAL_CART_ITEMS,
      digitalCartItems: getTypes.GET_ELECTRONIC_CART_ITEMS,
      customizationEmailTemplateIdentifier:
        getTypes.GET_REWARD_CUSTOMIZATION_ETID,
      cartExternalRefId: getTypes.GET_CART_EXTERNAL_REF_ID,
      cartItems: getTypes.GET_CART_ITEMS,
      orderIsProcessing: getTypes.GET_IS_PLACING_ORDER
    })
  },
  mounted () {
    this.$segment.track(CHECKOUT_STEP_VIEWED, { step: CHECKOUT_STEP_NUMBER })
  },
  methods: {
    cancelLeaveWhileProcessing () {
      this.showLeaveWhileProcessingConfirmation = false
      this.leaveWhileProcessingNextRoute = null
    },
    confirmLeaveWhileProcessing () {
      this.resetCartBeforeLeavingIfProcessingOrder()
      if (typeof this.leaveWhileProcessingNextRoute === 'function') {
        this.leaveWhileProcessingNextRoute()
      }
    },
    handleEditShippingAddress () {
      this.$router.push({ name: CHECKOUT_ROUTE })
    },
    async placeOrder (generateCaptchaToken) {
      if (this.termsOfServiceAgreedTo && !this.orderIsProcessing) {
        await this.postOrderRequest({
          externalReferenceId: this.cartExternalRefId,
          lineItems: this.mapCartItemsToLineItems(),
          generateCaptchaToken
        })

        this.$segment.track(CHECKOUT_STEP_COMPLETED, {
          step: CHECKOUT_STEP_NUMBER
        })
      }
    },
    mapCartItemsToLineItems () {
      return [
        ...this.mapDigitalCartItemsToLineItems(),
        ...this.mapPhysicalCartItemsToLineItems()
      ]
    },
    mapPhysicalCartItemsToLineItems () {
      const emailTemplateIdentifier =
        this.customizationEmailTemplateIdentifier || null
      return this.physicalCartItems.map(physicalCartItem =>
        LineItem.forPhysicalRecipient(
          physicalCartItem,
          this.physicalRecipient,
          this.digitalRecipient,
          emailTemplateIdentifier
        )
      )
    },
    mapDigitalCartItemsToLineItems () {
      const emailTemplateIdentifier =
        this.customizationEmailTemplateIdentifier || null
      return this.digitalCartItems.map(digitalCartItem =>
        LineItem.forDigitalRecipient(
          digitalCartItem,
          this.digitalRecipient,
          emailTemplateIdentifier
        )
      )
    },
    resetCartBeforeLeavingIfProcessingOrder () {
      if (this.orderIsProcessing) {
        this.resetCart()
      }
    },
    ...mapActions({
      postOrderRequest: actionTypes.PLACE_ORDER,
      resetCart: actionTypes.RESET_CART
    })
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/css/variables';

.review-and-complete {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-top: 72px;
  color: $tangoTextPrimary;

  .contact-information-container {
    padding: 0 60px 0 0;
    h4 {
      font-size: $font-size-md;
    }
  }

  .email-address {
    font-size: $font-size-md;
    color: $tangoTextPrimary;
  }

  .review-and-complete-shipping-address {
    margin-bottom: 40px;
  }

  .confirm-email-address-title {
    margin-top: 16px;
    margin-bottom: 5px;
    font-family: Gotham-Medium;
    color: $tangoTextPrimary;
  }
}

.checkout-review {
  margin-bottom: 30px;
  .cart-item {
    position: relative;

    .name {
      color: $tangoTextPrimary;
      font-family: Gotham-Medium, 'Helvetica Neue', Helvetica, Arial, sans-serif;
      font-size: $font-size-lg;
    }
  }
}

.order-review-and-complete-container {
  display: flex;
  flex-direction: column;
}

.contact-information-container {
  h4 {
    font-weight: bold;
    margin-bottom: 40px;
  }
}

.review-and-complete-tos-container {
  font-size: $font-size-md;
  display: flex;
  align-items: flex-start;
  padding-left: 10px;
  .review-and-complete-tos-checkbox {
    margin: 0;
  }
  .review-and-complete-tos-label {
    padding-top: 4px;
    label {
      cursor: pointer;
      display: inline;
    }
  }
}

@media screen and (max-width: $breakpoint-lg - 1) {
  .checkout-review {
    .cart-item {
      .name {
        font-size: $font-size-md;
      }
    }
  }

  .review-and-complete {
    flex-direction: column;
    padding: 30px 0 0;

    .contact-information-container {
      align-self: center;
      padding: 0;
    }
  }
}
</style>
